
import { ICacheableItem } from "../../Services/DataService/BaseCache";
import { DataServices, ICacheUpdateArg } from "../../Services/DataService/DataService";
import { BasicEntityWatcher } from "../../Services/DataService/EntityWatcher";
import { DataEntityBase } from "../../Services/DataService/Model";
import { IConstructorOptions, IEventArgsBase, IProperyChangedArgs } from "../../Utilities";
import { EntityManagerRegistrar } from "../Services/EntityManagerRegistrar";
import { HydrationBase } from "../Services/HydrationBase";
import { GetCorrelation, GLError, ICorrelation } from "../Utilities";

export interface IEntityConstructorOptions extends IConstructorOptions {
    dataEntity?: DataEntityBase;
    id?: string;
}

export interface IRefreshEvent extends IEventArgsBase {
    updates: DataEntityBase
}

export class BaseViewModel extends HydrationBase implements ICacheableItem {

    public static get TypeName() { return 'DataEntityBase' };
    public static get DirtyHydratePropertyName() { return 'DirtyHydrate' }

    protected constructor(options?: IEntityConstructorOptions) {
        super();

        this._watcher = DataServices.EntityWatcher;

        let data = options && options.dataEntity !== undefined ? options.dataEntity : this.CreateData(DataServices.EntityWatcher, options);
        if (data instanceof DataEntityBase)
            this.SetData(data!);

        DataServices.Raw.cachedItemChanged.Subscribe(this.onCachedItemChanged);
    }

    public get id() { return this.Data?.id ?? ''; }
    public get key() { return this.Data?.key ?? ''; }
    Serialize(): BaseViewModel {
        return this;
    }
    public get _PartitionKey() { return this.Data?._PartitionKey; }
    public get _writeVersion() { return this.Data?.writeVersion; }


    public ReplaceData(data: DataEntityBase) {

        let oldData = this._data;

        this._watcher!.UnregisterEntity(this._data!);
        this._data?.PropertyChanged.Unsubscribe(this.onChildPropertyChanged);
        this.SetData(data);

        this.onReplacedData(oldData, data);

        let differences = oldData?.IdentifyDifferences(data);

        this.FireChanged(`Data`);
        for (let diff of differences ?? []) {
            this.FireChanged(`Data/${diff}`);
        }

        this.Hydrate(GetCorrelation(), true);
    }



    /***************************************************************************************************/
    //
    // Protected
    //
    /***************************************************************************************************/



    protected onReplacedData(_oldData?: DataEntityBase, _newData?: DataEntityBase) {
        throw new Error('onReplacedData must be implemented in derived classes');
    }



    protected onCachedItemChanged = async (onCachedItemChanged: ICacheUpdateArg) => {

        let filtered = onCachedItemChanged.items.filter((item) => item.id === this.Data?.id);
        if (filtered.length === 1) {
            let newItem = filtered[0];
            let options = { sourceObject: newItem };
            let createdItem = this.CreateData(this._watcher!, options);
            this.ReplaceData(createdItem);
        }
        else if (filtered.length > 1)
            throw new Error('Multiple items with the same id found in the cache');

    }

    protected CreateData(_watcher: BasicEntityWatcher, _options?: IConstructorOptions): DataEntityBase {
        throw new Error('CreateData must be implemented in derived classes');
    }

    protected SetData(data: DataEntityBase) {
        this._data = data;
        this._watcher!.RegisterEntity(this._data);
        this._data.PropertyChanged.Subscribe(this.onChildPropertyChanged);
    }
    public get Data() { return this._data; }









    /***************************************************************************************************/
    //
    // CRUD Operations
    //
    /***************************************************************************************************/
    /**************************************************************************
     * 
     * Delete
     * 
     *************************************************************************/
    public async Delete(correlation: ICorrelation) {

        if (this.__isDeleted)
            return;
        let Track = BaseViewModel.deTelemetryManager?.GetTimedEventTracker('Delete', correlation);
        this.__isDeleted = true;
        //
        // First remove from cache/server
        //
        if (!this.Data)
            throw new GLError(
                {
                    telemetryMgr: BaseViewModel.deTelemetryManager,
                    id: '3a03ab16-7953-4d34-8d2b-730d52e94ad5',
                    name: 'Delete'
                }, 'Deleting an item that has no data.'
            )
        await EntityManagerRegistrar.GetManager(this.Data!.typeName)!.DeleteItem(correlation, this);

        //
        // Intentional invalid the data so that callers will have to correctly
        // deal with the item being deleted.
        //
        this._data = (undefined as unknown) as DataEntityBase;
        Track && Track();
    }


    public readonly __TEST_HOOKS__ = {
        ForceHydated: () => { this.SetHydrated(); }
    };




    /***************************************************************************************************/
    //
    // Privates
    //
    /***************************************************************************************************/
    private _data?: DataEntityBase = undefined;
    private onChildPropertyChanged = (args: IProperyChangedArgs) => {
        if (this.__isDeleted)
            throw new GLError(
                {
                    telemetryMgr: BaseViewModel.deTelemetryManager,
                    id: '3a03ab16-7953-4d34-8d2b-730d52e94ad5',
                    name: 'onChildPropertyChange'
                }, 'Modifying the property of a deleted item.'
            )
        this.FireChanged(`Data/${args.property}`);
    }

    private static deTelemetryManager: any | undefined = undefined;

    private __isDeleted = false;
    private _watcher: BasicEntityWatcher | undefined = undefined;
}